import { useState, useEffect, useMemo } from "react";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifFormField,
  MotifRadioButton,
  MotifRadioButtonGroup,
  MotifInput,
  MotifChip,
} from "@ey-xd/motif-react";

import {
  formatNumberWithZeroPrefix,
  handleNumber,
} from "../../../utils/common";

import "./ApproveReturnModal.scss";

export interface SelectedReturnData {
  TransactionReturnId: number;
  TransactionId: number;
  OrderTransactionQuantity: number;
  RequestQuantity: number;
  SkuId: string;
  OrderTransactionTotalCost: number;
  UsageInterval: string;
  UnitCost: string;
  ReqReturnAmount: number;
  Metric: string;
  CostMetricCd: string;
  UnitsUsed: number;
  ReturnableUnit: number;
}

export default function ApproveReturnModal({
  show,
  setShow,
  approveHandler,
  selectedReturnData,
  isApiLoading,
}: {
  show: boolean;
  setShow: (isVisible: boolean) => void;
  selectedSku?: string;
  approveHandler: (selectedRefundOption: string, approvedQty: number) => void;
  btnLabel: string;
  payload?: string;
  selectedReturnData: SelectedReturnData;
  isApiLoading: boolean;
}) {
  const args = {
    disabled: false,
    supportText: "",
    withError: false,
    required: false,
  };

  const { supportText, required } = args;

  const [selectedOption, setSelectedOption] = useState("");
  const [refundUnit, setRefundUnit] = useState("");
  const [refundUnitError, setRefundUnitError] = useState("");

  useEffect(() => {
    setSelectedOption("");
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
    setRefundUnitError("");
    setRefundUnit("");
  };

  const handleUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (refundUnitError) setRefundUnitError("");
    const value = handleNumber(event.target.value);
    setRefundUnit(value);
    const isOnlyDot = value.trim() === ".";
    if (isOnlyDot) {
      setRefundUnitError("Please enter a valid return unit.");
    }
    if (Number(value) === Number(selectedReturnData.RequestQuantity)) {
      setRefundUnitError(
        "Enter a different amount than the requested return units. Remember to only include one decimal.",
      );
    }
  };

  const handleUnitQuantityBlur = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    const val = formatNumberWithZeroPrefix(value);
    setRefundUnit(val);
  };

  const approveReturn = async () => {
    if (selectedOption === "Approve as requested") {
      // full refund
      if (
        selectedReturnData.RequestQuantity ===
        selectedReturnData.OrderTransactionQuantity
      ) {
        approveHandler(
          "Full Refund",
          Number(selectedReturnData.RequestQuantity),
        );
        return;
      } else {
        approveHandler(
          "Partial Refund",
          Number(selectedReturnData.RequestQuantity),
        );
      }
    } else {
      if (
        refundUnit === selectedReturnData.OrderTransactionQuantity.toString()
      ) {
        approveHandler("Full Refund", Number(refundUnit));
        return;
      } else {
        approveHandler("Partial Refund", Number(refundUnit));
      }
    }
  };

  const closeHandler = () => {
    setShow(false);
    setSelectedOption("");
    setRefundUnit("");
    setRefundUnitError("");
  };

  const disableButton = useMemo(() => {
    if (refundUnitError !== "") return true;
    if (!selectedOption) return true;
    if (selectedOption === "Approve with adjusted value") {
      const unit = Number(refundUnit);
      return (
        !unit ||
        refundUnit === "" ||
        unit <= 0 ||
        unit > selectedReturnData.OrderTransactionQuantity
      );
    }
    return false;
  }, [selectedOption, refundUnit, selectedReturnData.OrderTransactionQuantity]);

  const getNewReturnAmount = () => {
    if (refundUnit === "" || refundUnit == "." || refundUnit == "0.") return "";
    else {
      return `$${(Number(refundUnit) * Number(selectedReturnData.UnitCost.split(" ")[0])).toFixed(2)}`;
    }
  };

  const formatMetricByQuantity = (quantity: number, metric: string): string => {
    if (quantity === undefined || metric === undefined) {
      return "--";
    }
    if (quantity > 1) {
      return metric;
    }
    return metric.slice(0, -1);
  };

  const getSKUBadge = (CostMetricCd: string) => {
    if (CostMetricCd?.startsWith("var")) {
      return (
        <MotifChip
          className="sku-badge-variable"
          onClick={() => {}}
          disabled={false}
        >
          Variable
        </MotifChip>
      );
    } else if (CostMetricCd?.startsWith("fix")) {
      return (
        <MotifChip
          className="sku-badge-fixed"
          onClick={() => {}}
          disabled={false}
        >
          Fixed
        </MotifChip>
      );
    }
  };

  return (
    <MotifModal
      className="approve-return-order-modal"
      id="approveOrderModal"
      show={show}
      onClose={closeHandler}
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
    >
      <MotifModalHeader>{"Return Approval"}</MotifModalHeader>
      <MotifModalBody>
        <div className="content">
          You are about to approve the return for the following SKU. Please
          select the type of return.
        </div>
        <table className="return-approval-table motif-table-standard">
          <thead>
            <tr>
              <th>Committed U.S. Processing Charge</th>
              <th>Transaction</th>
              <th className="align-right">Units used</th>
              <th className="cost align-right">Total cost</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {selectedReturnData.SkuId}{" "}
                {getSKUBadge(selectedReturnData.CostMetricCd)}
              </td>
              <td>{selectedReturnData.UsageInterval}</td>
              <td className="align-right">
                {`${selectedReturnData.UnitsUsed} ${formatMetricByQuantity(selectedReturnData.UnitsUsed, selectedReturnData.Metric)}`}
              </td>
              <td className="cost align-right">
                $
                {Number(selectedReturnData.OrderTransactionTotalCost).toFixed(
                  2,
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <MotifFormField className="radioGroup">
          <MotifRadioButtonGroup>
            <MotifRadioButton
              value="Approve as requested"
              checked={selectedOption === "Approve as requested"}
              name="refund"
              id="refundChoice1"
              onChange={(event) => handleChange(event)}
              supportText={supportText}
              required={required}
            >
              Requested refund - Approve as requested
            </MotifRadioButton>
            <div className="partial-return-section">
              <p className="unit-text">{`Returnable units: ${selectedReturnData.ReturnableUnit} ${formatMetricByQuantity(selectedReturnData.ReturnableUnit, selectedReturnData.Metric)}`}</p>
              <p className="unit-text">{`Requested return units: ${selectedReturnData.RequestQuantity} ${formatMetricByQuantity(selectedReturnData.RequestQuantity, selectedReturnData.Metric)}`}</p>
              <p className="unit-text">{`Return Amount: $${Number(selectedReturnData.ReqReturnAmount).toFixed(2)}`}</p>
            </div>
            <MotifRadioButton
              value="Approve with adjusted value"
              checked={selectedOption === "Approve with adjusted value"}
              name="refund"
              id="refundChoice2"
              onChange={(event) => handleChange(event)}
              supportText={supportText}
              required={required}
            >
              Approve with adjusted value
            </MotifRadioButton>
            {selectedOption === "Approve with adjusted value" && (
              <div className="full-refund-section">
                <div className="adjusted-value-input">
                  <MotifInput
                    type="text"
                    className={`quantity-input ${refundUnitError !== "" ? "input-invalid" : ""}`}
                    value={refundUnit}
                    autoComplete="off"
                    onChange={handleUnitChange}
                    onBlur={handleUnitQuantityBlur}
                    onClear={() => setRefundUnit("")}
                  />
                  <span className="unit-text">Return Units</span>
                </div>
                {refundUnitError ? (
                  <div className="error-text">{refundUnitError}</div>
                ) : (
                  <div className="helper-text">
                    Return units can only include one decimal.
                  </div>
                )}

                <div className="new-return-amount">{`New return amount: ${getNewReturnAmount()}`}</div>
              </div>
            )}
          </MotifRadioButtonGroup>
        </MotifFormField>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant={"primary"}
          onClick={closeHandler}
          disabled={isApiLoading}
          className="cancelButton"
        >
          Cancel
        </MotifButton>
        <MotifButton
          variant={"primary"}
          onClick={approveReturn}
          disabled={disableButton}
          loading={isApiLoading}
          className="approveButton"
        >
          Approve
        </MotifButton>
      </MotifModalFooter>
    </MotifModal>
  );
}
